import posthog from 'posthog-js';

// Telemetria do login do portal (PostHog, MESMO projeto do dashboard-web:
// "Dashboard Hermes"). O cookie de identificação do PostHog é gravado no
// domínio pai (.abbiamolog.com), então o mesmo aparelho tem o mesmo id aqui e
// no log/go — dá pra seguir "abriu o log sem sessão → portal → logou? →
// voltou pro log logado?" num funil só.
//
// Motivo (15/10/2026): no Safari do iPhone, 90% de quem abre o log direto
// chega sem sessão, e quase ninguém volta logado. O portal não tinha nenhum
// evento, então não dava pra saber se a pessoa desiste no login, se o login
// falha, ou se loga e o cookie da sessão não fica.
//
// Sem PII nas propriedades: nada de e-mail, senha, token ou valor de cookie.
// Quem é a pessoa vem só do `identify` no sucesso, como o dashboard-web faz.

const apiKey = process.env['NX_POSTHOG_KEY'];
const apiHost = process.env['NX_POSTHOG_HOST'];

let enabled = false;

export const initTelemetry = (): void => {
  if (!apiKey || typeof window === 'undefined') return;
  posthog.init(apiKey, {
    ...(apiHost ? { api_host: apiHost } : {}),
    // Só os eventos do funil e os pageviews: nada de autocapture de clique,
    // que no formulário de login não acrescenta e só gera volume.
    autocapture: false,
    capture_pageview: 'history_change',
    disable_session_recording: true,
    person_profiles: 'identified_only',
  });
  // De onde a pessoa chegou vale pra todos os eventos desta carga.
  posthog.register(loginEntryContext());
  enabled = true;
};

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider';

// O que o navegador REALMENTE guardou da sessão neste momento — sem valores.
// É o que separa "logou e o cookie ficou" de "logou e o cookie sumiu".
export const cognitoStorageSnapshot = () => {
  const cookies =
    typeof document === 'undefined'
      ? []
      : document.cookie
          .split(';')
          .map((c) => c.trim())
          .filter((c) => c.startsWith(COGNITO_PREFIX));
  let localStorageKeys = 0;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      if (window.localStorage.key(i)?.startsWith(COGNITO_PREFIX)) {
        localStorageKeys += 1;
      }
    }
  } catch {
    // localStorage indisponível (modo privado antigo, bloqueio): fica 0.
  }
  return {
    cognito_cookie_count: cookies.length,
    cognito_cookie_bytes: cookies.reduce((sum, c) => sum + c.length, 0),
    cognito_cookie_max_bytes: cookies.reduce(
      (max, c) => Math.max(max, c.length),
      0,
    ),
    cognito_local_storage_keys: localStorageKeys,
    cookies_enabled:
      typeof navigator === 'undefined' ? undefined : navigator.cookieEnabled,
  };
};

// Contexto de toda tela de login: de onde a pessoa veio e o que pediu.
export const loginEntryContext = () => {
  const params = new URLSearchParams(window.location.search);
  let referrerHost = '';
  try {
    referrerHost = document.referrer ? new URL(document.referrer).host : '';
  } catch {
    referrerHost = '';
  }
  return {
    referrer_host: referrerHost,
    requested_product: params.get('product') ?? undefined,
    has_return_to: params.has('return_to'),
    standalone_display:
      window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true,
  };
};

type LoginStage =
  | 'signin'
  | 'new_password'
  | 'mfa_challenge'
  | 'mfa_setup'
  | 'access_validation';

export type PortalEvent =
  | { name: 'portal_login_viewed' }
  | { name: 'portal_login_session_found' }
  | { name: 'portal_login_attempt' }
  | { name: 'portal_login_challenge'; challenge: string }
  | {
      name: 'portal_login_error';
      stage: LoginStage;
      // Nome da exceção do Cognito (NotAuthorizedException, ...) ou o código
      // da API do /me. Nunca a mensagem crua, que pode ecoar o e-mail.
      error_code: string;
    }
  | { name: 'portal_login_success' }
  | { name: 'portal_auth_check'; result: 'authenticated' | 'unauthenticated' }
  | {
      name: 'portal_product_redirect';
      product: string;
      handoff: 'shared_cookie' | 'id_token' | 'no_session';
      target_host: string;
    };

export const track = (event: PortalEvent): void => {
  if (!enabled) return;
  const { name, ...props } = event;
  posthog.capture(
    name,
    { ...props, ...cognitoStorageSnapshot() },
    // Vários destes eventos saem logo antes de trocar de página (redirect pro
    // produto); sendBeacon é o que garante que chegam.
    { transport: 'sendBeacon' },
  );
};

// Mesmo id que o dashboard-web usa (`posthog.identify(user.email)` com o
// e-mail do /me, sem normalizar): assim a pessoa é uma só nos dois apps.
export const identifyUser = (email: string | undefined): void => {
  if (!enabled || !email) return;
  posthog.identify(email);
};

export const errorCodeOf = (err: unknown): string => {
  const e = err as { code?: unknown; name?: unknown } | null;
  if (e && typeof e.code === 'string' && e.code) return e.code;
  if (e && typeof e.name === 'string' && e.name) return e.name;
  return 'unknown';
};

export const hostOf = (url: string): string => {
  try {
    return new URL(url, window.location.href).host;
  } catch {
    return '';
  }
};
